import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

type QueryWithData<T> = UseQueryResult<T[]> & { data: T[] };
type SupabaseQueryResult<T = unknown> = { data: T | null; error: unknown };
type QueryBuilderLike = PromiseLike<SupabaseQueryResult<unknown>> & {
  select: (...args: unknown[]) => QueryBuilderLike;
  eq: (...args: unknown[]) => QueryBuilderLike;
  order: (...args: unknown[]) => QueryBuilderLike;
  insert: (...args: unknown[]) => QueryBuilderLike;
  update: (...args: unknown[]) => QueryBuilderLike;
  single: () => Promise<SupabaseQueryResult<unknown>>;
};
type SupabaseTableClient = { from: (table: string) => QueryBuilderLike };
type SupabaseRpcClient = {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<SupabaseQueryResult<unknown>>;
};

const tableClient = (supabase: SupabaseClientLike): SupabaseTableClient =>
  supabase as unknown as SupabaseTableClient;

const rpcClient = (supabase: SupabaseClientLike): SupabaseRpcClient =>
  supabase as unknown as SupabaseRpcClient;

// =============================================================================
// Types — L90 Procurement & Vendor Management
// =============================================================================

export interface Vendor {
  id: string;
  tenant_id: string;
  company_id: string | null;
  vendor_code: string;
  vendor_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  payment_terms: string | null;
  average_lead_days: number | null;
  rating: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PurchaseRequisition {
  id: string;
  tenant_id: string;
  requisition_number: string;
  requester_id: string | null;
  description: string;
  status: 'draft' | 'submitted' | 'approved' | 'rfq_sent' | 'completed';
  total_estimated_cost: number | null;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RequisitionItem {
  id: string;
  requisition_id: string;
  item_code: string | null;
  description: string;
  quantity: number;
  unit_price: number | null;
  unit_of_measure: string | null;
  estimated_cost: number | null;
  created_at: string;
}

export interface RFQRequest {
  id: string;
  tenant_id: string;
  requisition_id: string | null;
  rfq_number: string;
  status: 'draft' | 'sent' | 'responses_received' | 'evaluated' | 'awarded';
  description: string;
  sent_at: string | null;
  response_deadline: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RFQVendorInvitation {
  id: string;
  rfq_id: string;
  vendor_id: string;
  invited_at: string | null;
  response_received_at: string | null;
  response_cost: number | null;
  response_delivery_days: number | null;
  response_notes: string | null;
  response_attachment_url: string | null;
  status: 'pending' | 'responded' | 'declined' | 'expired';
  created_at: string;
}

export interface PurchaseOrder {
  id: string;
  tenant_id: string;
  po_number: string;
  vendor_id: string;
  supplier_id?: string | null;
  requisition_id: string | null;
  rfq_id: string | null;
  status:
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'ordered'
    | 'received'
    | 'draft'
    | 'issued'
    | 'confirmed'
    | 'delivered'
    | 'invoiced'
    | 'paid';
  total_cost: number;
  total_amount: number;
  delivery_date: string | null;
  created_by: string | null;
  issued_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface POLineItem {
  id: string;
  po_id: string;
  item_code: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  unit_of_measure: string | null;
  line_total: number;
  created_at: string;
}

export interface PurchaseReceipt {
  id: string;
  tenant_id: string;
  po_id: string;
  receipt_number: string;
  received_at: string;
  received_by: string | null;
  notes: string | null;
  status?: string;
  created_at: string;
}

export interface ReceiptLineItem {
  id: string;
  receipt_id: string;
  po_line_item_id: string | null;
  quantity_received: number;
  quantity_accepted: number;
  quantity_rejected: number;
  rejection_reason: string | null;
  created_at: string;
}

export interface ThreeWayMatchResult {
  status: 'auto_approved' | 'manual_review';
  variance_pct: number;
  approval_required: boolean;
  po_qty: number;
  receipt_qty: number;
}

// =============================================================================
// Query Keys
// =============================================================================

export const procurementKeys = {
  all: ['procurement'] as const,
  vendors: (tenantId: string) => ['procurement', 'vendors', tenantId] as const,
  requisitions: (tenantId: string) => ['procurement', 'requisitions', tenantId] as const,
  requisitionItems: (requisitionId: string) =>
    ['procurement', 'requisition-items', requisitionId] as const,
  rfqs: (tenantId: string) => ['procurement', 'rfqs', tenantId] as const,
  rfqVendorInvitations: (rfqId: string) =>
    ['procurement', 'rfq-vendor-invitations', rfqId] as const,
  orders: (tenantId: string) => ['procurement', 'orders', tenantId] as const,
  poLineItems: (poId: string) => ['procurement', 'po-line-items', poId] as const,
  receipts: (tenantId: string) => ['procurement', 'receipts', tenantId] as const,
  receiptLineItems: (receiptId: string) =>
    ['procurement', 'receipt-line-items', receiptId] as const,
} as const;

// =============================================================================
// Hooks — Vendors
// =============================================================================

export function useVendors(supabase: SupabaseClientLike, tenantId: string): QueryWithData<Vendor> {
  return useQuery({
    queryKey: procurementKeys.vendors(tenantId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('vendors')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('vendor_name', { ascending: true });
      if (error) throw error;
      return data as Vendor[];
    },
    enabled: Boolean(tenantId),
  }) as QueryWithData<Vendor>;
}

export interface CreateVendorInput {
  vendor_code: string;
  vendor_name: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  payment_terms?: string | null;
  average_lead_days?: number | null;
}

export function useCreateVendor(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateVendorInput) => {
      const { data, error } = await tableClient(supabase)
        .from('vendors')
        .insert({
          tenant_id: tenantId,
          vendor_code: input.vendor_code,
          vendor_name: input.vendor_name,
          contact_email: input.contact_email ?? null,
          contact_phone: input.contact_phone ?? null,
          payment_terms: input.payment_terms ?? null,
          average_lead_days: input.average_lead_days ?? null,
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Vendor;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.vendors(tenantId) }),
  });
}

// =============================================================================
// Hooks — Requisitions
// =============================================================================

export function usePurchaseRequisitions(
  supabase: SupabaseClientLike,
  tenantId: string,
): QueryWithData<PurchaseRequisition> {
  return useQuery({
    queryKey: procurementKeys.requisitions(tenantId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_requisitions')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PurchaseRequisition[];
    },
    enabled: Boolean(tenantId),
  }) as QueryWithData<PurchaseRequisition>;
}

export function useRequisitionItems(
  supabase: SupabaseClientLike,
  requisitionId: string,
): QueryWithData<RequisitionItem> {
  return useQuery({
    queryKey: procurementKeys.requisitionItems(requisitionId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('requisition_items')
        .select('*')
        .eq('requisition_id', requisitionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as RequisitionItem[];
    },
    enabled: Boolean(requisitionId),
  }) as QueryWithData<RequisitionItem>;
}

export interface CreateRequisitionInput {
  requisition_number: string;
  description: string;
  items: Array<{
    item_code?: string;
    description: string;
    quantity: number;
    unit_price?: number;
    unit_of_measure?: string;
  }>;
}

export function useCreateRequisition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRequisitionInput) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_requisitions')
        .insert({
          tenant_id: tenantId,
          requisition_number: input.requisition_number,
          description: input.description,
          status: 'draft',
          total_estimated_cost: input.items.reduce(
            (sum, item) => sum + (item.unit_price ?? 0) * item.quantity,
            0,
          ),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.requisitions(tenantId) }),
  });
}

export interface UpdateRequisitionInput {
  description?: string;
  status?: PurchaseRequisition['status'];
  total_estimated_cost?: number;
}

export function useUpdateRequisition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateRequisitionInput }) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_requisitions')
        .update(input)
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as PurchaseRequisition;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.requisitions(tenantId) }),
  });
}

// =============================================================================
// Hooks — RFQs
// =============================================================================

export function useRFQRequests(
  supabase: SupabaseClientLike,
  tenantId: string,
): QueryWithData<RFQRequest> {
  return useQuery({
    queryKey: procurementKeys.rfqs(tenantId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('rfq_requests')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as RFQRequest[];
    },
    enabled: Boolean(tenantId),
  }) as QueryWithData<RFQRequest>;
}

export function useRFQVendorInvitations(
  supabase: SupabaseClientLike,
  rfqId: string,
): QueryWithData<RFQVendorInvitation> {
  return useQuery({
    queryKey: procurementKeys.rfqVendorInvitations(rfqId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('rfq_vendor_invitations')
        .select('*')
        .eq('rfq_id', rfqId)
        .order('invited_at', { ascending: false });
      if (error) throw error;
      return data as RFQVendorInvitation[];
    },
    enabled: Boolean(rfqId),
  }) as QueryWithData<RFQVendorInvitation>;
}

export interface CreateRFQInput {
  rfq_number: string;
  description: string;
  requisition_id?: string;
  response_deadline?: string;
}

export function useCreateRFQ(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRFQInput) => {
      const { data, error } = await tableClient(supabase)
        .from('rfq_requests')
        .insert({
          tenant_id: tenantId,
          rfq_number: input.rfq_number,
          description: input.description,
          requisition_id: input.requisition_id,
          response_deadline: input.response_deadline,
          status: 'draft',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.rfqs(tenantId) }),
  });
}

export interface SendRFQToVendorsInput {
  rfq_id: string;
  vendor_ids: string[];
}

export function useSendRFQToVendors(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendRFQToVendorsInput) => {
      const invitations = input.vendor_ids.map((vid) => ({
        rfq_id: input.rfq_id,
        vendor_id: vid,
        invited_at: new Date().toISOString(),
        status: 'pending',
      }));
      const { error } = await tableClient(supabase)
        .from('rfq_vendor_invitations')
        .insert(invitations);
      if (error) throw error;
      // Update RFQ status
      await tableClient(supabase)
        .from('rfq_requests')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', input.rfq_id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: procurementKeys.rfqs(tenantId) });
    },
  });
}

// =============================================================================
// Hooks — Purchase Orders
// =============================================================================

export function usePurchaseOrders(
  supabase: SupabaseClientLike,
  tenantId: string,
): QueryWithData<PurchaseOrder> {
  return useQuery({
    queryKey: procurementKeys.orders(tenantId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PurchaseOrder[];
    },
    enabled: Boolean(tenantId),
  }) as QueryWithData<PurchaseOrder>;
}

export function usePOLineItems(
  supabase: SupabaseClientLike,
  poId: string,
): QueryWithData<POLineItem> {
  return useQuery({
    queryKey: procurementKeys.poLineItems(poId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('po_line_items')
        .select('*')
        .eq('po_id', poId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as POLineItem[];
    },
    enabled: Boolean(poId),
  }) as QueryWithData<POLineItem>;
}

export interface CreatePurchaseOrderInput {
  po_number: string;
  vendor_id?: string | null;
  supplier_id?: string | null;
  total_cost?: number;
  total_amount?: number;
  delivery_date?: string;
  requisition_id?: string;
  rfq_id?: string;
  items?: Array<{
    item_code?: string;
    description: string;
    quantity: number;
    unit_price: number;
    unit_of_measure?: string;
  }>;
  notes?: string | null;
}

export function useCreatePurchaseOrder(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePurchaseOrderInput) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .insert({
          tenant_id: tenantId,
          po_number: input.po_number,
          vendor_id: input.vendor_id ?? input.supplier_id ?? '',
          total_cost: input.total_cost ?? input.total_amount ?? 0,
          total_amount: input.total_amount ?? input.total_cost ?? 0,
          delivery_date: input.delivery_date,
          requisition_id: input.requisition_id,
          rfq_id: input.rfq_id,
          status: 'draft',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.orders(tenantId) }),
  });
}

export function useIssuePurchaseOrder(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (poId: string) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .update({
          status: 'issued',
          issued_at: new Date().toISOString(),
        })
        .eq('id', poId)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.orders(tenantId) }),
  });
}

function useUpdatePurchaseOrderStatus(
  supabase: SupabaseClientLike,
  tenantId: string,
  status: PurchaseOrder['status'],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (poId: string) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .update({ status })
        .eq('id', poId)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as PurchaseOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.orders(tenantId) }),
  });
}

export interface UpdatePurchaseOrderInput {
  vendor_id?: string;
  status?: PurchaseOrder['status'];
  notes?: string;
  expected_delivery_date?: string;
  delivery_date?: string;
  total_cost?: number;
  total_amount?: number;
}

export function useUpdatePurchaseOrder(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePurchaseOrderInput }) => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .update({
          ...input,
          delivery_date: input.expected_delivery_date ?? input.delivery_date,
        })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as PurchaseOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: procurementKeys.orders(tenantId) }),
  });
}

export function useApprovePurchaseOrder(supabase: SupabaseClientLike, tenantId: string) {
  return useUpdatePurchaseOrderStatus(supabase, tenantId, 'approved');
}

export function useRejectPurchaseOrder(supabase: SupabaseClientLike, tenantId: string) {
  return useUpdatePurchaseOrderStatus(supabase, tenantId, 'rejected');
}

export function useMarkPOOrdered(supabase: SupabaseClientLike, tenantId: string) {
  return useUpdatePurchaseOrderStatus(supabase, tenantId, 'ordered');
}

export function useMarkPOReceived(supabase: SupabaseClientLike, tenantId: string) {
  return useUpdatePurchaseOrderStatus(supabase, tenantId, 'received');
}

// =============================================================================
// Hooks — Receipts & 3-Way Matching
// =============================================================================

export function usePurchaseReceipts(
  supabase: SupabaseClientLike,
  tenantId: string,
): QueryWithData<PurchaseReceipt> {
  return useQuery({
    queryKey: procurementKeys.receipts(tenantId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('purchase_receipts')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('received_at', { ascending: false });
      if (error) throw error;
      return data as PurchaseReceipt[];
    },
    enabled: Boolean(tenantId),
  }) as QueryWithData<PurchaseReceipt>;
}

export function useReceiptLineItems(
  supabase: SupabaseClientLike,
  receiptId: string,
): QueryWithData<ReceiptLineItem> {
  return useQuery({
    queryKey: procurementKeys.receiptLineItems(receiptId),
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('receipt_line_items')
        .select('*')
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as ReceiptLineItem[];
    },
    enabled: Boolean(receiptId),
  }) as QueryWithData<ReceiptLineItem>;
}

export interface ReceivePOInput {
  po_id: string;
  receipt_number: string;
  items: Array<{
    po_line_item_id?: string;
    quantity_received: number;
    quantity_accepted: number;
    quantity_rejected?: number;
    rejection_reason?: string;
  }>;
  notes?: string;
}

export function useReceivePO(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReceivePOInput) => {
      const { data: receipt, error: rcError } = await tableClient(supabase)
        .from('purchase_receipts')
        .insert({
          tenant_id: tenantId,
          po_id: input.po_id,
          receipt_number: input.receipt_number,
          notes: input.notes,
        })
        .select()
        .single();
      if (rcError) throw rcError;
      const createdReceipt = receipt as PurchaseReceipt;

      // Insert receipt line items
      const items = input.items.map((item) => ({
        receipt_id: createdReceipt.id,
        po_line_item_id: item.po_line_item_id,
        quantity_received: item.quantity_received,
        quantity_accepted: item.quantity_accepted,
        quantity_rejected: item.quantity_rejected ?? 0,
        rejection_reason: item.rejection_reason,
      }));
      const { error: itemError } = await tableClient(supabase)
        .from('receipt_line_items')
        .insert(items);
      if (itemError) throw itemError;

      return createdReceipt;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: procurementKeys.receipts(tenantId) });
      qc.invalidateQueries({ queryKey: procurementKeys.orders(tenantId) });
    },
  });
}

export function useMatchStatus(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: ['procurement', 'match-status', tenantId],
    queryFn: async () => {
      const { data, error } = await rpcClient(supabase).rpc('validate_3way_match', {
        p_po_id: '',
        p_receipt_id: '',
      });
      if (error) throw error;
      return data as ThreeWayMatchResult;
    },
    enabled: Boolean(tenantId),
  });
}

export function useValidate3WayMatch(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: async ({ poId, receiptId }: { poId: string; receiptId: string }) => {
      const { data, error } = await rpcClient(supabase).rpc('validate_3way_match', {
        p_po_id: poId,
        p_receipt_id: receiptId,
      });
      if (error) throw error;
      return data as ThreeWayMatchResult;
    },
  });
}

// =============================================================================
// Approval Workflow — A10
// =============================================================================

export const PROCUREMENT_APPROVAL_THRESHOLDS = {
  LEVEL_1_MAX: 1000, // < 1000 GEL: manager only
  LEVEL_2_MAX: 10000, // < 10000 GEL: manager + admin
  // >= 10000 GEL: manager + admin + owner
} as const;

export interface ApprovalRequest {
  id: string;
  tenant_id: string;
  requisition_id: string;
  approver_id: string;
  level: 1 | 2 | 3;
  status: 'pending' | 'approved' | 'rejected';
  comment: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface SubmitRequisitionInput {
  requisition_id: string;
  total_estimated_cost: number;
  /** level-1 approver (manager). Pass employee's manager_id or fallback to first admin. */
  manager_id: string;
  /** level-2 approver (admin). First admin found in tenant_memberships. */
  admin_id: string;
  /** level-3 approver (owner). First owner found in tenant_memberships. */
  owner_id: string;
}

type TenantMemberRow = { user_id: string; role_name: string };

export function useSubmitRequisition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitRequisitionInput) => {
      const { total_estimated_cost, requisition_id, manager_id, admin_id, owner_id } = input;

      // Determine required levels from thresholds
      const rows: Array<{
        tenant_id: string;
        requisition_id: string;
        approver_id: string;
        level: number;
      }> = [{ tenant_id: tenantId, requisition_id, approver_id: manager_id, level: 1 }];
      if (total_estimated_cost >= PROCUREMENT_APPROVAL_THRESHOLDS.LEVEL_1_MAX) {
        rows.push({ tenant_id: tenantId, requisition_id, approver_id: admin_id, level: 2 });
      }
      if (total_estimated_cost >= PROCUREMENT_APPROVAL_THRESHOLDS.LEVEL_2_MAX) {
        rows.push({ tenant_id: tenantId, requisition_id, approver_id: owner_id, level: 3 });
      }

      const { error: insertError } = await tableClient(supabase)
        .from('approval_requests')
        .insert(rows);
      if (insertError) throw insertError;

      const { data, error: updateError } = await tableClient(supabase)
        .from('purchase_requisitions')
        .update({ status: 'submitted', submitted_at: new Date().toISOString() })
        .eq('id', requisition_id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (updateError) throw updateError;
      return data as PurchaseRequisition;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: procurementKeys.requisitions(tenantId) });
      // TODO v2: support multiple approvers per level (currently first match only)
    },
  });
}

/** Resolves manager/admin/owner IDs for submit. Returns null if not resolvable. */
export function useResolveApprovers(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: ['procurement', 'approvers', tenantId],
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('tenant_memberships')
        .select('user_id, role_name')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const members = (data ?? []) as TenantMemberRow[];
      const admin = members.find((m) => m.role_name === 'admin');
      const owner = members.find((m) => m.role_name === 'owner');
      return {
        admin_id: admin?.user_id ?? null,
        owner_id: owner?.user_id ?? null,
      };
    },
    enabled: Boolean(tenantId),
  });
}

export function useApproveRequisition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      approvalRequestId,
      requisitionId,
      comment,
    }: {
      approvalRequestId: string;
      requisitionId: string;
      comment?: string;
    }) => {
      const { error: approvalError } = await tableClient(supabase)
        .from('approval_requests')
        .update({
          status: 'approved',
          comment: comment ?? null,
          decided_at: new Date().toISOString(),
        })
        .eq('id', approvalRequestId)
        .eq('tenant_id', tenantId);
      if (approvalError) throw approvalError;

      // Check if all levels are approved → mark requisition approved
      const { data: allRequests, error: fetchError } = await tableClient(supabase)
        .from('approval_requests')
        .select('status')
        .eq('requisition_id', requisitionId)
        .eq('tenant_id', tenantId);
      if (fetchError) throw fetchError;

      const requests = (allRequests ?? []) as Array<{ status: string }>;
      const allApproved = requests.every((r) => r.status === 'approved');
      if (allApproved) {
        const { error: reqError } = await tableClient(supabase)
          .from('purchase_requisitions')
          .update({ status: 'approved', approved_at: new Date().toISOString() })
          .eq('id', requisitionId)
          .eq('tenant_id', tenantId);
        if (reqError) throw reqError;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: procurementKeys.requisitions(tenantId) });
      qc.invalidateQueries({ queryKey: ['procurement', 'approvals', tenantId] });
    },
  });
}

export function useRejectRequisition(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      approvalRequestId,
      requisitionId,
      comment,
    }: {
      approvalRequestId: string;
      requisitionId: string;
      comment: string;
    }) => {
      const { error: approvalError } = await tableClient(supabase)
        .from('approval_requests')
        .update({
          status: 'rejected',
          comment,
          decided_at: new Date().toISOString(),
        })
        .eq('id', approvalRequestId)
        .eq('tenant_id', tenantId);
      if (approvalError) throw approvalError;

      const { error: reqError } = await tableClient(supabase)
        .from('purchase_requisitions')
        .update({ status: 'submitted' })
        .eq('id', requisitionId)
        .eq('tenant_id', tenantId);
      if (reqError) throw reqError;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: procurementKeys.requisitions(tenantId) });
      qc.invalidateQueries({ queryKey: ['procurement', 'approvals', tenantId] });
    },
  });
}

export function usePendingApprovals(
  supabase: SupabaseClientLike,
  tenantId: string,
  userId: string,
): QueryWithData<ApprovalRequest> {
  return useQuery({
    queryKey: ['procurement', 'approvals', tenantId, 'pending', userId],
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('approval_requests')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('approver_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ApprovalRequest[];
    },
    enabled: Boolean(tenantId && userId),
  }) as QueryWithData<ApprovalRequest>;
}

export function useRequisitionApprovals(
  supabase: SupabaseClientLike,
  tenantId: string,
  requisitionId: string,
): QueryWithData<ApprovalRequest> {
  return useQuery({
    queryKey: ['procurement', 'approvals', tenantId, 'requisition', requisitionId],
    queryFn: async () => {
      const { data, error } = await tableClient(supabase)
        .from('approval_requests')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('requisition_id', requisitionId)
        .order('level', { ascending: true });
      if (error) throw error;
      return data as ApprovalRequest[];
    },
    enabled: Boolean(tenantId && requisitionId),
  }) as QueryWithData<ApprovalRequest>;
}

// =============================================================================
// Hook — Vendor Performance
// =============================================================================

export function useVendorPerformance(
  supabase: SupabaseClientLike,
  tenantId: string,
  vendorId: string,
) {
  return useQuery({
    queryKey: ['procurement', 'vendor-performance', vendorId],
    queryFn: async () => {
      // Aggregate vendor PO/receipt data
      const { data, error } = await tableClient(supabase)
        .from('purchase_orders')
        .select('id, status, total_cost, issued_at, delivered_at')
        .eq('tenant_id', tenantId)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const orders = data as PurchaseOrder[];
      const totalOrders = orders.length;
      const deliveredOrders = orders.filter(
        (po) => po.status === 'delivered' || po.status === 'invoiced' || po.status === 'paid',
      ).length;
      const totalSpent = orders.reduce((sum, po) => sum + (po.total_cost ?? 0), 0);
      const onTimeRate = totalOrders > 0 ? Math.round((deliveredOrders / totalOrders) * 100) : 0;

      return {
        totalOrders,
        deliveredOrders,
        totalSpent,
        onTimeRate,
        averageOrderValue: totalOrders > 0 ? Math.round(totalSpent / totalOrders) : 0,
      };
    },
    enabled: Boolean(tenantId && vendorId),
  });
}
